// Supply-provider capability declarations, mirrored from Phantom's MCP
// contract (PHANTOM_MCP_TOOLS.md, search_restaurants envelope +
// get_restaurant_detail). The block declares the provider's data semantics —
// address handling, hours knowledge, item-availability granularity, pricing
// shape, payment owner, rate limiting, fixed search radius — so UI branches
// on what the provider DECLARES instead of which backend it assumes it is
// talking to (the doordash branch's ORDER_PROVIDER env flag, retired by this).
//
// An absent/partial block means an older Phantom: assume the platform
// defaults below, which reproduce the app's historical behavior exactly.

export const PLATFORM_CAPABILITY_DEFAULTS = Object.freeze({
  supply: 'platform',
  addresses: 'free_form',
  hours: 'known',
  item_availability: 'per_item',
  pricing: 'computed',
  payment: 'platform_stripe',
  // Who settles with the supply side (Phantom NETWORK_SUPPLY_DESIGN.md §2):
  // mealops_pays_restaurant (onboarded restaurants, Stripe Connect payout),
  // mealops_pays_network (the network bills MealOps — DoorDash), or
  // customer_pays_merchant (the merchant charges the eater directly; Phantom
  // refuses start_payment/submit for these until its handoff flow lands).
  // The customer side is always MealOps regardless of this value.
  settlement: 'mealops_pays_restaurant',
  rate_limited: false,
  search_radius_miles: null,
  // How the price filter works: 'rows' (rows carry their price; filter the
  // rows held) or 'search' (the nearby list carries no price; a price pick
  // is a price_tiers search, one provider call, and every row it returns
  // answers it). 'search': a coach's own DoorDash account (2026-10-19).
  price_filter: 'rows',
});

const ENUMS = {
  supply: ['platform', 'marketplace'],
  addresses: ['free_form', 'provider_saved'],
  hours: ['known', 'unknown'],
  item_availability: ['per_item', 'store_level_only'],
  pricing: ['computed', 'verbatim_line_items'],
  payment: ['platform_stripe', 'provider_on_file'],
  settlement: ['mealops_pays_restaurant', 'mealops_pays_network', 'customer_pays_merchant'],
  price_filter: ['rows', 'search'],
};

/**
 * Normalize a raw capabilities block into a complete declaration. Unknown
 * values fall back to the platform default for that field — a future Phantom
 * introducing a new enum value degrades to today's behavior rather than
 * crashing or half-applying.
 */
export function normalizeProviderCapabilities(raw) {
  const capabilities = { ...PLATFORM_CAPABILITY_DEFAULTS };
  if (!raw || typeof raw !== 'object') return capabilities;

  for (const [field, allowed] of Object.entries(ENUMS)) {
    if (allowed.includes(raw[field])) capabilities[field] = raw[field];
  }
  if (typeof raw.rate_limited === 'boolean') capabilities.rate_limited = raw.rate_limited;
  if (raw.search_radius_miles === null || Number.isFinite(Number(raw.search_radius_miles))) {
    capabilities.search_radius_miles =
      raw.search_radius_miles === null ? null : Number(raw.search_radius_miles);
  }
  return capabilities;
}
